import React, { useState } from 'react';
import Link from '../components/LocalizedLink';

import { ArrowLeft, Check, X, Sparkles, Zap, Crown, Building2, Info, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import PackageRequestModal from '../components/PackageRequestModal';

const ListinoPage = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handlePackageSelect = (pacchetto) => {
    setSelectedPackage({
      nome: pacchetto.nome,
      iniziale: pacchetto.unaTantum,
      mensile: pacchetto.mensile,
      dettagli: pacchetto.descrizione
    });
    setShowModal(true);
  };

  const scontoLancio = 0.20;
  const applicaSconto = (prezzo) => Math.round(prezzo * (1 - scontoLancio));

  // Feature keys for i18n - ordered
  const FEATURE_KEYS = ['sito', 'design', 'contatti', 'db', 'login', 'automazioni', 'api', 'custom', 'scalable', 'consulting'];
  const SERVICE_KEYS = ['hosting', 'assistance', 'updates'];

  const pacchetti = [
    {
      nome: t('tariffe.packages.lite.nome'),
      icon: Sparkles,
      colore: 'var(--color-accent)',
      unaTantumOriginale: 15,
      unaTantum: applicaSconto(15),
      mensile: 19.99,
      tipoProgetto: t('tariffe.packages.lite.tipoProgetto'),
      descrizione: t('listino.descriptions.lite'),
      prezzoVariabile: true,
      features: {
        sito: true, design: true, contatti: true, db: false, login: false,
        automazioni: false, api: false, custom: false, scalable: false, consulting: false
      },
      servizi: {
        hosting: t('listino.svals.base'),
        assistance: t('listino.svals.base'),
        updates: t('listino.svals.dash')
      },
      dettagliAssistenza: 'base'
    },
    {
      nome: t('tariffe.packages.standard.nome'),
      icon: Zap,
      colore: 'var(--color-primary)',
      unaTantumOriginale: 45,
      unaTantum: applicaSconto(45),
      mensile: 35,
      tipoProgetto: t('tariffe.packages.standard.tipoProgetto'),
      descrizione: t('listino.descriptions.standard'),
      consigliato: true,
      prezzoVariabile: true,
      features: {
        sito: true, design: true, contatti: true, db: true, login: true,
        automazioni: true, api: false, custom: false, scalable: false, consulting: false
      },
      servizi: {
        hosting: t('listino.svals.base'),
        assistance: t('listino.svals.base'),
        updates: '2'
      },
      dettagliAssistenza: 'base'
    },
    {
      nome: t('tariffe.packages.pro.nome'),
      icon: Crown,
      colore: '#8B5CF6',
      unaTantumOriginale: 150,
      unaTantum: applicaSconto(150),
      mensile: 85,
      aPartireDa: true,
      tipoProgetto: t('tariffe.packages.pro.tipoProgetto'),
      descrizione: t('listino.descriptions.pro'),
      prezzoVariabile: true,
      features: {
        sito: true, design: true, contatti: true, db: true, login: true,
        automazioni: true, api: true, custom: true, scalable: false, consulting: false
      },
      servizi: {
        hosting: t('listino.svals.premium'),
        assistance: t('listino.svals.pro'),
        updates: '4'
      },
      dettagliAssistenza: 'pro'
    },
    {
      nome: t('tariffe.packages.enterprise.nome'),
      icon: Building2,
      colore: '#1e40af',
      unaTantumOriginale: 500,
      unaTantum: applicaSconto(500),
      mensile: 199,
      aPartireDa: true,
      tipoProgetto: t('tariffe.packages.enterprise.tipoProgetto'),
      descrizione: t('listino.descriptions.enterprise'),
      prezzoVariabile: true,
      features: {
        sito: true, design: true, contatti: true, db: true, login: true,
        automazioni: true, api: true, custom: true, scalable: true, consulting: true
      },
      servizi: {
        hosting: t('listino.svals.dedicato'),
        assistance: t('listino.svals.enterprise_247'),
        updates: t('listino.svals.unlimited')
      },
      dettagliAssistenza: 'enterprise'
    }
  ];

  return (
    <div data-testid="listino-page" className="min-h-screen bg-[var(--color-bg)]">
      {/* Banner Sconto Lancio */}
      <div className="bg-emerald-500 text-white py-3 px-4 text-center">
        <p className="text-sm font-semibold">
          {t('listino.banner')}
        </p>
      </div>
      
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <Link 
          to="/tariffe" 
          className="inline-flex items-center text-[var(--color-primary)] hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.torna_calcolatore')}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl md:text-4xl font-extrabold text-[var(--color-text)] mb-2">
            {t('listino.title')}
          </h1>
          <p className="text-[var(--color-text-muted)] mb-8 max-w-2xl">
            {t('listino.subtitle')}
          </p>
        </motion.div>

        {/* Pricing Cards - Mobile */}
        <div className="md:hidden space-y-6 mb-8">
          {pacchetti.map((pacchetto, index) => {
            const Icona = pacchetto.icon;
            return (
              <motion.div
                key={pacchetto.nome}
                className={`card-elevated p-6 ${pacchetto.consigliato ? 'border-2 border-[var(--color-primary)]' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {pacchetto.consigliato && (
                  <span className="inline-block bg-[var(--color-primary)] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                    {t('listino.most_popular')}
                  </span>
                )}
                
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${pacchetto.colore}15` }}
                  >
                    <Icona className="w-5 h-5" style={{ color: pacchetto.colore }} />
                  </div>
                  <h2 className="text-xl font-bold text-[var(--color-text)]">{pacchetto.nome}</h2>
                </div>
                
                <p className="text-sm text-[var(--color-text-muted)] mb-4">
                  <span className="font-semibold" style={{ color: pacchetto.colore }}>{pacchetto.tipoProgetto}</span>
                  {' — '}{pacchetto.descrizione}
                </p>
                
                <div className="p-4 bg-[var(--color-surface)] rounded-xl mb-6">
                  <div className="inline-block bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full mb-2">
                    {t('listino.launch_20')}
                  </div>
                  
                  {pacchetto.aPartireDa && (
                    <p className="text-xs text-[var(--color-text-muted)] mb-1">{t('tariffe.starting_from')}</p>
                  )}
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-bold text-[var(--color-text)]">{pacchetto.unaTantum}€</span>
                    <span className="text-sm text-[var(--color-text-muted)] line-through">{pacchetto.unaTantumOriginale}€</span>
                    <span className="text-[var(--color-text-muted)]">+</span>
                    <span className="text-xl font-bold" style={{ color: pacchetto.colore }}>{pacchetto.mensile}€</span>
                    <span className="text-sm text-[var(--color-text-muted)]">{t('tariffe.per_month')}</span>
                  </div>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    {t('tariffe.variable_price')}
                  </p>
                </div>

                <div className="mb-4">
                  <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">{t('listino.functions_included')}</p>
                  <ul className="space-y-2">
                    {FEATURE_KEYS.map((featureKey) => {
                      const isIncluded = pacchetto.features[featureKey];
                      const label = t(`listino.features.${featureKey}`);
                      return (
                        <li key={featureKey} className={`flex items-center gap-2 text-sm ${isIncluded ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)] line-through'}`}>
                          {isIncluded ? (
                            <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mb-6">
                  <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">{t('listino.services_in_fee')}</p>
                  <ul className="space-y-2">
                    {SERVICE_KEYS.map((serviceKey) => {
                      const value = pacchetto.servizi[serviceKey];
                      return (
                        <li key={serviceKey} className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                          <Check className="w-4 h-4 flex-shrink-0" style={{ color: pacchetto.colore }} />
                          <span>{t(`listino.services.${serviceKey}`)}: {value}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <Button 
                  className={`w-full rounded-full ${pacchetto.consigliato ? 'btn-primary' : ''}`}
                  variant={pacchetto.consigliato ? 'default' : 'outline'}
                  onClick={() => handlePackageSelect(pacchetto)}
                  data-testid={`mobile-select-${pacchetto.nome.toLowerCase()}-btn`}
                >
                  {t('listino.choose', { name: pacchetto.nome })}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Comparison Table - Desktop */}
        <motion.div 
          className="hidden md:block card-elevated overflow-hidden mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="p-4 text-left text-[var(--color-text-muted)] font-medium"></th>
                {pacchetti.map((pacchetto) => {
                  const Icona = pacchetto.icon;
                  return (
                    <th key={pacchetto.nome} className="p-4 text-center relative">
                      {pacchetto.consigliato && (
                        <span className="absolute -top-0 left-1/2 -translate-x-1/2 bg-[var(--color-primary)] text-white text-xs font-bold px-2 py-0.5 rounded-b-lg">
                          {t('listino.most_popular')}
                        </span>
                      )}
                      <div className="flex flex-col items-center gap-2">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${pacchetto.colore}15` }}
                        >
                          <Icona className="w-5 h-5" style={{ color: pacchetto.colore }} />
                        </div>
                        <span className="font-bold text-[var(--color-text)]">{pacchetto.nome}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-surface)]">
                <td className="p-4 font-medium text-[var(--color-text)]">
                  <div className="flex items-center gap-2">
                    {t('listino.price')}
                    <span className="inline-block bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      -20%
                    </span>
                  </div>
                </td>
                {pacchetti.map((pacchetto) => (
                  <td key={pacchetto.nome} className="p-4 text-center">
                    {pacchetto.aPartireDa && (
                      <p className="text-xs text-[var(--color-text-muted)] mb-1">{t('tariffe.starting_from')}</p>
                    )}
                    <div className="flex items-baseline justify-center gap-1 flex-wrap">
                      <span className="text-xl font-bold text-[var(--color-text)]">{pacchetto.unaTantum}€</span>
                      <span className="text-sm text-[var(--color-text-muted)] line-through">{pacchetto.unaTantumOriginale}€</span>
                      <span className="text-[var(--color-text-muted)]">+</span>
                      <span className="text-xl font-bold" style={{ color: pacchetto.colore }}>{pacchetto.mensile}€</span>
                      <span className="text-sm text-[var(--color-text-muted)]">{t('tariffe.per_month')}</span>
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">{t('listino.variable_project')}</p>
                  </td>
                ))}
              </tr>

              <tr className="border-b border-[var(--color-border)]">
                <td className="p-4 font-medium text-[var(--color-text)]">{t('listino.project_type')}</td>
                {pacchetti.map((pacchetto) => (
                  <td key={pacchetto.nome} className="p-4 text-center">
                    <span className="text-sm font-semibold" style={{ color: pacchetto.colore }}>
                      {pacchetto.tipoProgetto}
                    </span>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">{pacchetto.descrizione}</p>
                  </td>
                ))}
              </tr>

              <tr className="bg-[var(--color-bg)]">
                <td colSpan={5} className="p-3 text-xs font-bold text-[var(--color-text-muted)] uppercase tracking-wide">
                  {t('listino.what_is_included')}
                </td>
              </tr>
              
              {FEATURE_KEYS.map((featureKey, index) => (
                <tr key={featureKey} className={index !== FEATURE_KEYS.length - 1 ? 'border-b border-[var(--color-border)]' : ''}>
                  <td className="p-4 text-[var(--color-text)]">{t(`listino.features.${featureKey}`)}</td>
                  {pacchetti.map((pacchetto) => {
                    const value = pacchetto.features[featureKey];
                    return (
                      <td key={pacchetto.nome} className="p-4 text-center">
                        {value ? (
                          <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-gray-400 mx-auto" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              <tr className="bg-[var(--color-bg)]">
                <td colSpan={5} className="p-3 text-xs font-bold text-[var(--color-text-muted)] uppercase tracking-wide">
                  {t('listino.services_monthly')}
                </td>
              </tr>

              {SERVICE_KEYS.map((serviceKey, index) => (
                <tr key={serviceKey} className={index !== SERVICE_KEYS.length - 1 ? 'border-b border-[var(--color-border)]' : ''}>
                  <td className="p-4 text-[var(--color-text)]">{t(`listino.services.${serviceKey}`)}</td>
                  {pacchetti.map((pacchetto) => {
                    const value = pacchetto.servizi[serviceKey];
                    return (
                      <td key={pacchetto.nome} className="p-4 text-center">
                        <span className="text-sm font-medium text-[var(--color-text)]">{value}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        <motion.div 
          className="hidden md:flex justify-center gap-4 mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {pacchetti.map((pacchetto) => (
            <Button 
              key={pacchetto.nome}
              className={`rounded-full px-8 ${pacchetto.consigliato ? 'btn-primary' : ''}`}
              variant={pacchetto.consigliato ? 'default' : 'outline'}
              onClick={() => handlePackageSelect(pacchetto)}
              data-testid={`desktop-select-${pacchetto.nome.toLowerCase()}-btn`}
            >
              {t('listino.choose', { name: pacchetto.nome })}
            </Button>
          ))}
        </motion.div>

        <motion.div 
          className="p-6 bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-[var(--color-primary)]" />
            <h2 className="font-bold text-[var(--color-text)]">{t('listino.payment_title')}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="p-4 bg-[var(--color-bg)] rounded-lg">
              <p className="font-semibold text-[var(--color-text)] mb-2">{t('listino.initial_cost_title')}</p>
              <p className="text-[var(--color-text-muted)]">
                {t('listino.initial_cost_desc')}
              </p>
            </div>
            
            <div className="p-4 bg-[var(--color-primary-light)] rounded-lg">
              <p className="font-semibold text-[var(--color-text)] mb-2">{t('listino.monthly_fee_title')}</p>
              <p className="text-[var(--color-text-muted)]">
                {t('listino.monthly_fee_desc')}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="text-center mt-8 space-y-3">
          <Link to="/tariffe">
            <Button variant="ghost" className="text-[var(--color-primary)]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.torna_calcolatore')}
            </Button>
          </Link>
          
          <div>
            <Link 
              to="/tariffe/configuratore" 
              className="inline-flex items-center gap-2 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
            >
              <Settings className="w-3 h-3" />
              {t('listino.custom_link')}
            </Link>
          </div>
        </div>
      </div>

      <PackageRequestModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        packageInfo={selectedPackage || { nome: '', iniziale: 0, mensile: 0 }}
      />
    </div>
  );
};

export default ListinoPage;
